import type { ReactNode } from "react";
import type { Fulfilment } from "../../lib/fulfilment";
import { formatPesos } from "../../lib/money";
import { Card } from "../ui";

export interface OrderSummaryProps {
  itemCount: number;
  subtotalCents: number;
  fulfilment?: Fulfilment;
  paymentMethod?: "bank_transfer" | "cash_on_delivery";
  /** False for an account with no email: staff text the delivery charge instead (PRD 5.1). */
  emailed?: boolean;
  /** Checkout lists what's being ordered; the basket shows its own lines instead. */
  lines?: { id: number; name: string; quantity: string; totalCents: number }[];
  /** The action and anything under it. */
  children?: ReactNode;
}

/**
 * The totals card for the basket and checkout. Prices include tax, so nothing is added for it.
 * Delivery has no rate yet (PRD 5.8): staff quote it on the order, so it is never in this total.
 */
export function OrderSummary({ itemCount, subtotalCents, fulfilment = "delivery", paymentMethod = "bank_transfer", emailed = true, lines, children }: OrderSummaryProps) {
  const quoted = fulfilment === "delivery" || fulfilment === "terminal_pickup";
  const totalLabel =
    paymentMethod === "cash_on_delivery" ? (fulfilment === "pickup" ? "To pay when you collect" : "To pay on delivery") : quoted ? "Parts total" : "Total to pay now";
  return (
    <Card padding="var(--space-6)">
      <p className="mb-4 type-eyebrow tracking-[var(--tracking-widest)] text-ink-500 uppercase">Order summary</p>
      {lines && (
        <ul className="m-0 mb-4 flex list-none flex-col gap-2 border-b border-border-subtle p-0 pb-4">
          {lines.map((line) => (
            <li key={line.id} className="flex justify-between gap-4 type-small">
              <span className="min-w-0 text-text-body">
                {line.name} <span className="whitespace-nowrap text-text-muted">× {line.quantity}</span>
              </span>
              <span className="type-sku text-text-strong">{formatPesos(line.totalCents)}</span>
            </li>
          ))}
        </ul>
      )}
      <dl className="m-0 flex flex-col gap-3 type-body">
        <div className="flex justify-between gap-4">
          <dt>
            Parts · {itemCount} {itemCount === 1 ? "item" : "items"}
          </dt>
          <dd className="m-0 type-sku">{formatPesos(subtotalCents)}</dd>
        </div>
        <div className="flex justify-between gap-4 text-text-muted">
          <dt>{fulfilment === "pickup" ? "Pickup" : "Delivery"}</dt>
          <dd className="m-0 text-right type-small">{fulfilment === "pickup" ? "You collect it" : fulfilment === "urgent_delivery" ? "Paid to the rider" : "Quoted separately"}</dd>
        </div>
        <div className="flex items-baseline justify-between gap-4 border-t border-border-subtle pt-3">
          <dt className="type-body-strong">{totalLabel}</dt>
          <dd className="m-0 type-price [font-size:var(--text-xl)] text-text-strong">{formatPesos(subtotalCents)}</dd>
        </div>
      </dl>
      <p className="mt-2 mb-0 type-small text-text-muted">
        Prices include tax.
        {quoted && ` We'll ${emailed ? "email" : "text"} you the delivery charge${fulfilment === "terminal_pickup" ? ", bus fee included" : ""}, ${paymentMethod === "cash_on_delivery" ? "which you pay with the parts" : "before you pay"}.`}
        {fulfilment === "urgent_delivery" && " You pay the rider their fee directly when it arrives."}
      </p>
      {children && <div className="mt-6">{children}</div>}
    </Card>
  );
}
